import { AnimationConfig } from '@/types';

// Default animation configuration
export const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
  greenFlashDuration: 300, // milliseconds
  redDistortionDuration: 400, // milliseconds
  transitionSpeed: 150, // milliseconds for letter transitions
};

/**
 * Animation utility class for managing visual feedback
 * ENHANCED: Word-mode optimized animations
 */
export class AnimationManager {
  private animationConfig: AnimationConfig;
  private activeAnimations: Map<string, number> = new Map();
  private currentTrainingMode: 'letter' | 'word' = 'letter';

  constructor(config: AnimationConfig = DEFAULT_ANIMATION_CONFIG) {
    this.animationConfig = config;
  }

  /**
   * Set training mode for animation optimization
   */
  public setTrainingMode(mode: 'letter' | 'word', _currentSequence?: any[], _currentIndex?: number): void {
    this.currentTrainingMode = mode;
    // Note: _currentSequence and _currentIndex are accepted for compatibility but not stored
  }

  /**
   * Triggers a green flash animation for correct letter input
   * OPTIMIZED: Skip in word mode, save for word completion
   */
  public triggerGreenFlash(elementId: string): Promise<void> {
    // 🎯 OPTIMIZATION: Skip individual letter success animations in word mode
    if (this.currentTrainingMode === 'word') {
      // Just resolve immediately without animation
      return Promise.resolve();
    }
    
    // Continue with normal letter mode animation
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        resolve();
        return;
      }

      // Clear any existing animation
      this.clearAnimation(elementId);

      // Apply green flash styles
      element.style.transition = `all ${this.animationConfig.transitionSpeed}ms ease-out`;
      element.style.transform = 'scale(1.1)';
      element.style.boxShadow = `
        0 0 20px rgba(34, 197, 94, 0.8),
        0 0 40px rgba(34, 197, 94, 0.4),
        0 0 60px rgba(34, 197, 94, 0.2)
      `;
      element.style.backgroundColor = 'rgba(34, 197, 94, 0.3)';

      // Store animation timeout
      const timeoutId = window.setTimeout(() => {
        element.style.transform = 'scale(1)';
        element.style.boxShadow = 'none';
        element.style.backgroundColor = 'transparent';
        
        // Final cleanup after transition
        setTimeout(() => {
          element.style.transition = '';
          this.activeAnimations.delete(elementId);
          resolve();
        }, this.animationConfig.transitionSpeed);
      }, this.animationConfig.greenFlashDuration);

      this.activeAnimations.set(elementId, timeoutId);
    });
  }

  /**
   * Triggers a red distortion animation for incorrect letter input
   */
  public triggerRedDistortion(elementId: string): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        resolve();
        return;
      }

      // Clear any existing animation
      this.clearAnimation(elementId);

      // Apply red distortion styles
      element.style.transition = `all ${this.animationConfig.transitionSpeed}ms ease-out`;
      element.style.transform = 'scale(1.05) skewX(2deg)';
      element.style.filter = 'hue-rotate(15deg) brightness(1.2)';
      element.style.boxShadow = `
        0 0 15px rgba(239, 68, 68, 0.6),
        0 0 30px rgba(239, 68, 68, 0.3)
      `;
      element.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
      element.style.color = '#ef4444';

      // Apply shake animation
      this.applyShakeAnimation(element);

      // Store animation timeout
      const timeoutId = window.setTimeout(() => {
        element.style.transform = 'scale(1) skewX(0deg)';
        element.style.filter = 'none';
        element.style.boxShadow = 'none';
        element.style.backgroundColor = 'transparent';
        
        // Final cleanup after transition
        setTimeout(() => {
          element.style.transition = '';
          this.activeAnimations.delete(elementId);
          resolve();
        }, this.animationConfig.transitionSpeed);
      }, this.animationConfig.redDistortionDuration);

      this.activeAnimations.set(elementId, timeoutId);
    });
  }

  /**
   * Applies a subtle shake animation to an element
   */
  private applyShakeAnimation(element: HTMLElement): void {
    const keyframes = [
      { transform: 'translateX(0)' },
      { transform: 'translateX(-2px)' },
      { transform: 'translateX(2px)' },
      { transform: 'translateX(-1px)' },
      { transform: 'translateX(1px)' },
      { transform: 'translateX(0)' }
    ];

    element.animate(keyframes, {
      duration: 200,
      easing: 'ease-in-out'
    });
  }

  /**
   * Triggers smooth transition between letter sets
   */
  public triggerLetterTransition(
    containerId: string, 
    onTransitionMid?: () => void
  ): Promise<void> {
    return new Promise((resolve) => {
      const container = document.getElementById(containerId);
      if (!container) {
        onTransitionMid?.();
        resolve();
        return;
      }

      // Fade out
      container.style.transition = `opacity ${this.animationConfig.transitionSpeed}ms ease-out`;
      container.style.opacity = '0';

      setTimeout(() => {
        // Call mid-transition callback (usually to update content)
        onTransitionMid?.();
        
        // Fade in
        container.style.opacity = '1';
        
        setTimeout(() => {
          container.style.transition = '';
          resolve();
        }, this.animationConfig.transitionSpeed);
      }, this.animationConfig.transitionSpeed);
    });
  }

  /**
   * Clears any active animation for a specific element
   */
  private clearAnimation(elementId: string): void {
    const timeoutId = this.activeAnimations.get(elementId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.activeAnimations.delete(elementId);
    }

    // Reset element styles
    const element = document.getElementById(elementId);
    if (element) {
      element.style.transition = '';
      element.style.transform = '';
      element.style.boxShadow = '';
      element.style.backgroundColor = '';
      element.style.filter = '';
      element.style.color = '';
    }
  }

  /**
   * Clears all active animations
   */
  public clearAllAnimations(): void {
    this.activeAnimations.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.activeAnimations.clear();
  }

  /**
   * Updates animation configuration
   */
  public updateConfig(newConfig: Partial<AnimationConfig>): void {
    this.animationConfig = { ...this.animationConfig, ...newConfig };
  }

  /**
   * Gets current animation configuration
   */
  public getConfig(): AnimationConfig {
    return { ...this.animationConfig };
  }

  /**
   * NEW: Word completion animation with sound
   */
  public triggerWordCompletion(containerId: string): Promise<void> {
    return new Promise((resolve) => {
      const container = document.getElementById(containerId);
      if (!container) {
        resolve();
        return;
      }

      // Word completion celebration animation
      container.style.transition = `all ${this.animationConfig.transitionSpeed}ms ease-out`;
      container.style.transform = 'scale(1.1)';
      container.style.boxShadow = `
        0 0 30px rgba(34, 197, 94, 0.8),
        0 0 60px rgba(34, 197, 94, 0.4),
        0 0 90px rgba(34, 197, 94, 0.2)
      `;
      container.style.backgroundColor = 'rgba(34, 197, 94, 0.2)';
      
      // Play success sound (if available)
      this.playWordCompletionSound();
      
      // Animate back to normal
      const timeoutId = window.setTimeout(() => {
        container.style.transform = 'scale(1)';
        container.style.boxShadow = 'none';
        container.style.backgroundColor = 'transparent';
        
        setTimeout(() => {
          container.style.transition = '';
          resolve();
        }, this.animationConfig.transitionSpeed);
      }, this.animationConfig.greenFlashDuration);

      this.activeAnimations.set(containerId, timeoutId);
    });
  }

  // 🚀 PERFORMANCE: Cached AudioContext - creating one per keystroke was the #2 cause of training lag
  private static cachedAudioContext: AudioContext | null = null;

  /**
   * Lazy-init the shared AudioContext once (must be triggered by user gesture first)
   */
  private getAudioContext(): AudioContext | null {
    if (AnimationManager.cachedAudioContext && AnimationManager.cachedAudioContext.state !== 'closed') {
      return AnimationManager.cachedAudioContext;
    }
    try {
      AnimationManager.cachedAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      return AnimationManager.cachedAudioContext;
    } catch {
      return null;
    }
  }

  /**
   * NEW: Play word completion sound
   * 🚀 OPTIMIZED: Reuses cached AudioContext instead of creating new one per keystroke
   */
  private playWordCompletionSound(): void {
    try {
      const audioContext = this.getAudioContext();
      if (!audioContext) return;

      // Resume if suspended (browser autoplay policy)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Success chord: C-E-G (major triad)
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // Silently fail if audio is not supported
    }
  }

  /**
   * NEW: Check if current letter is the last in a word and trigger completion
   */
  public checkWordCompletion(letterIndex: number, sequence: any[]): boolean {
    if (this.currentTrainingMode !== 'word' || !sequence) {
      return false;
    }
    
    // Check if this is the last letter in the current sequence (word)
    const isLastLetter = letterIndex === sequence.length - 1;
    
    if (isLastLetter) {
      // Trigger word completion animation
      const containerId = 'letter-display-container';
      this.triggerWordCompletion(containerId);
      return true;
    }
    
    return false;
  }
}

// CSS utility functions for dynamic styling
export const createLetterStyles = {
  neutral: {
    color: '#ffffff',
    backgroundColor: 'transparent',
    border: '2px solid #374151',
    transform: 'scale(1)',
    transition: 'all 0.15s ease-out'
  },
  
  correct: {
    color: '#22c55e',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    border: '2px solid #22c55e',
    transform: 'scale(1)',
    transition: 'all 0.15s ease-out'
  },
  
  incorrect: {
    color: '#ef4444',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '2px solid #ef4444',
    transform: 'scale(1)',
    transition: 'all 0.15s ease-out'
  }
};

/**
 * Utility function to apply styles to an element
 */
export const applyStyles = (
  element: HTMLElement, 
  styles: Record<string, string>
): void => {
  Object.assign(element.style, styles);
};

/**
 * Creates a heartbeat-like pulse animation
 */
export const createHeartbeatAnimation = (element: HTMLElement): Animation => {
  const keyframes = [
    { transform: 'scale(1)' },
    { transform: 'scale(1.05)' },
    { transform: 'scale(1)' },
    { transform: 'scale(1.08)' },
    { transform: 'scale(1)' }
  ];

  return element.animate(keyframes, {
    duration: 600,
    easing: 'ease-in-out'
  });
};

/**
 * Creates a typing rhythm animation for progress tiles
 */
export const createProgressTileAnimation = (
  element: HTMLElement, 
  type: 'correct' | 'incorrect'
): Animation => {
  const keyframes = type === 'correct' 
    ? [
        { transform: 'scale(0) rotate(0deg)', opacity: 0 },
        { transform: 'scale(1.2) rotate(180deg)', opacity: 1 },
        { transform: 'scale(1) rotate(360deg)', opacity: 1 }
      ]
    : [
        { transform: 'scale(0)', opacity: 0 },
        { transform: 'scale(1.3)', opacity: 0.8 },
        { transform: 'scale(1)', opacity: 1 }
      ];

  return element.animate(keyframes, {
    duration: 300,
    easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
  });
};

// Global animation manager instance
export const animationManager = new AnimationManager();
